// Static mock data for the v6 build, ported from the Claude Design handoff
// (`data/horses.js` + `data/history.js`). Used to populate screens before they
// are wired to Firestore. The structure mirrors what real data will look like.
//
// "Now" is fixed at Sunday 24 May 2026 10:42 (matches the design canvas) — the
// dashboard accepts an optional `now` prop so tests can pin time too.

import type { HorseColour } from "./avatarTones";
import { computePace, paceFraction } from "./hydration";
import type { PaceStatus } from "./hydration";

export const MOCK_NOW_HOUR = 10 + 42 / 60;
export const MOCK_TODAY = new Date(2026, 4, 24);

export const MOCK_YARD = {
  name: "Hollow Tree Livery",
  postcode: "GL7 5JJ",
  manager: "Sarah",
  date: "Sunday 24 May",
  weatherC: 22,
  nowHour: MOCK_NOW_HOUR,
} as const;

export interface MockFeed {
  type: string;
  kg: number;
}

export interface MockTodayLog {
  exercise: { mins: number; intensity: string | null };
  feed: MockFeed[];
  behaviour: string;
  droppings: string;
  droppingsCount: number;
  notes: string;
}

export interface MockHorse {
  id: string;
  name: string;
  colour: HorseColour;
  breed: string;
  age: number;
  weightKg: number;
  // Today's running intake and the EHE-derived daily target.
  waterL: number;
  targetL: number;
  // Minutes since the last log was written. null = never logged.
  lastLoggedMin: number | null;
  // If a horse has no log today, optionally show yesterday's intake as the ghost bar.
  lastKnownL?: number;
  flags: {
    droppings?: boolean;
    behaviour?: boolean;
    combined?: boolean;
  };
  history5: (PaceStatus | null)[];
  streakDays?: number;
  today: MockTodayLog | null;
}

export const MOCK_HORSES: MockHorse[] = [
  {
    id: "henry",
    name: "Henry",
    colour: "bay",
    breed: "Irish Sport Horse",
    age: 12,
    weightKg: 580,
    waterL: 4,
    targetL: 50,
    lastLoggedMin: 28,
    flags: { droppings: true, behaviour: true, combined: true },
    history5: ["hydrated", "hydrated", "mild", "moderate", "severe"],
    streakDays: 0,
    today: {
      exercise: { mins: 0, intensity: null },
      feed: [{ type: "Hay", kg: 6 }, { type: "Hard feed", kg: 2 }],
      behaviour: "Restless",
      droppings: "None seen",
      droppingsCount: 0,
      notes: "Off his water since yesterday evening. Pawing intermittently.",
    },
  },
  {
    id: "storm",
    name: "Storm",
    colour: "grey",
    breed: "Connemara",
    age: 9,
    weightKg: 460,
    waterL: 9,
    targetL: 48,
    lastLoggedMin: 18,
    flags: { droppings: true },
    history5: ["hydrated", "hydrated", "mild", "mild", "moderate"],
    streakDays: 0,
    today: {
      exercise: { mins: 30, intensity: "Light" },
      feed: [{ type: "Haylage", kg: 7 }],
      behaviour: "Dull",
      droppings: "Soft / loose",
      droppingsCount: 2,
      notes: "",
    },
  },
  {
    id: "folly",
    name: "Folly",
    colour: "palomino",
    breed: "Welsh Section D",
    age: 8,
    weightKg: 480,
    waterL: 28,
    targetL: 40,
    lastLoggedMin: 26 * 60,
    lastKnownL: 28,
    flags: {},
    history5: ["hydrated", "hydrated", "hydrated", null, null],
    today: null,
  },
  {
    id: "banjo",
    name: "Banjo",
    colour: "piebald",
    breed: "Cob",
    age: 11,
    weightKg: 520,
    waterL: 30,
    targetL: 45,
    lastLoggedMin: 28 * 60,
    lastKnownL: 30,
    flags: {},
    history5: ["hydrated", "hydrated", "mild", null, null],
    today: null,
  },
  {
    id: "murphy",
    name: "Murphy",
    colour: "bay",
    breed: "Irish Draught",
    age: 14,
    weightKg: 620,
    waterL: 11,
    targetL: 45,
    lastLoggedMin: 70,
    flags: {},
    history5: ["hydrated", "hydrated", "mild", "mild", "mild"],
    streakDays: 0,
    today: {
      exercise: { mins: 60, intensity: "Medium" },
      feed: [{ type: "Hay", kg: 7 }, { type: "Hard feed", kg: 2 }],
      behaviour: "Bright",
      droppings: "Normal",
      droppingsCount: 5,
      notes: "",
    },
  },
  {
    id: "willow",
    name: "Willow",
    colour: "chestnut",
    breed: "Thoroughbred",
    age: 7,
    weightKg: 510,
    waterL: 10,
    targetL: 40,
    lastLoggedMin: 55,
    flags: {},
    history5: ["mild", "hydrated", "hydrated", "hydrated", "mild"],
    streakDays: 0,
    today: {
      exercise: { mins: 45, intensity: "Medium" },
      feed: [{ type: "Hay", kg: 5 }, { type: "Hard feed", kg: 1.5 }],
      behaviour: "Normal",
      droppings: "Normal",
      droppingsCount: 4,
      notes: "",
    },
  },
  {
    id: "bramble",
    name: "Bramble",
    colour: "chestnut",
    breed: "Cob X",
    age: 16,
    weightKg: 540,
    waterL: 17,
    targetL: 40,
    lastLoggedMin: 60,
    flags: {},
    history5: ["hydrated", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 7,
    today: {
      exercise: { mins: 20, intensity: "Light" },
      feed: [{ type: "Hay", kg: 6 }],
      behaviour: "Normal",
      droppings: "Normal",
      droppingsCount: 5,
      notes: "",
    },
  },
  {
    id: "bella",
    name: "Bella",
    colour: "bay",
    breed: "Hanoverian",
    age: 10,
    weightKg: 550,
    waterL: 14,
    targetL: 45,
    lastLoggedMin: 22,
    flags: {},
    history5: ["hydrated", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 9,
    today: {
      exercise: { mins: 45, intensity: "Medium" },
      feed: [{ type: "Hay", kg: 6 }, { type: "Hard feed", kg: 2 }],
      behaviour: "Bright",
      droppings: "Normal",
      droppingsCount: 4,
      notes: "Hacked out with Pip. Drank well before turnout.",
    },
  },
  {
    id: "rosie",
    name: "Rosie",
    colour: "grey",
    breed: "Welsh Cob",
    age: 13,
    weightKg: 470,
    waterL: 12,
    targetL: 42,
    lastLoggedMin: 40,
    flags: {},
    history5: ["hydrated", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 12,
    today: {
      exercise: { mins: 0, intensity: null },
      feed: [{ type: "Hay", kg: 5 }],
      behaviour: "Bright",
      droppings: "Normal",
      droppingsCount: 6,
      notes: "",
    },
  },
  {
    id: "pickle",
    name: "Pickle",
    colour: "dun",
    breed: "New Forest",
    age: 18,
    weightKg: 410,
    waterL: 10,
    targetL: 35,
    lastLoggedMin: 15,
    flags: {},
    history5: ["mild", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 4,
    today: {
      exercise: { mins: 30, intensity: "Light" },
      feed: [{ type: "Hay", kg: 4 }],
      behaviour: "Normal",
      droppings: "Normal",
      droppingsCount: 4,
      notes: "",
    },
  },
  {
    id: "magic",
    name: "Magic",
    colour: "black",
    breed: "Friesian X",
    age: 9,
    weightKg: 560,
    waterL: 11,
    targetL: 40,
    lastLoggedMin: 50,
    flags: {},
    history5: ["hydrated", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 6,
    today: {
      exercise: { mins: 50, intensity: "Medium" },
      feed: [{ type: "Haylage", kg: 6 }],
      behaviour: "Bright",
      droppings: "Normal",
      droppingsCount: 5,
      notes: "",
    },
  },
  {
    id: "otto",
    name: "Otto",
    colour: "roan",
    breed: "Warmblood",
    age: 11,
    weightKg: 590,
    waterL: 10,
    targetL: 38,
    lastLoggedMin: 35,
    flags: {},
    history5: ["mild", "hydrated", "hydrated", "hydrated", "hydrated"],
    streakDays: 4,
    today: {
      exercise: { mins: 40, intensity: "Medium" },
      feed: [{ type: "Hay", kg: 6 }, { type: "Hard feed", kg: 1.5 }],
      behaviour: "Normal",
      droppings: "Normal",
      droppingsCount: 4,
      notes: "",
    },
  },
];

// ----- Per-horse 14-day history (for the Horse Profile Timeline + Trends) -----

export interface MockHistoryDay {
  date: Date;
  dateLabel: string;
  dayShort: string;
  intakeL: number;
  targetL: number;
  status: PaceStatus;
  behaviour: string;
  droppings: string;
  exerciseMins: number;
  exerciseIntensity: string;
  isToday: boolean;
  partial: boolean;
}

interface Recipe {
  intakes: number[];
  behaviours: string[];
  droppings: string[];
  exMins: number[];
  exInt: string[];
}

function makeBella(): Recipe {
  return {
    intakes: [43, 42, 38, 45, 36, 42, 44, 40, 38, 41, 45, 43, 44, 14],
    behaviours: ["bright", "normal", "off", "bright", "off", "normal", "bright", "normal", "off", "normal", "bright", "bright", "normal", "bright"],
    droppings: Array(14).fill("normal"),
    exMins: [30, 45, 60, 20, 0, 45, 30, 60, 40, 30, 45, 30, 20, 45],
    exInt: ["medium", "medium", "intense", "light", "", "medium", "light", "intense", "medium", "light", "medium", "light", "light", "medium"],
  };
}

function makeHenry(): Recipe {
  const padded = [50, 49, 50, 48, 49, 46, 50, 48, 47, 44, 38, 32, 28, 4];
  return {
    intakes: padded,
    behaviours: ["bright", "normal", "bright", "normal", "normal", "normal", "off", "off", "off", "off", "dull", "off", "dull", "restless"],
    droppings: ["normal", "normal", "normal", "normal", "normal", "normal", "normal", "normal", "normal", "normal", "soft-loose", "soft-loose", "hard-dry", "none-seen"],
    exMins: [45, 60, 30, 45, 45, 45, 20, 30, 30, 0, 20, 0, 0, 0],
    exInt: ["medium", "medium", "light", "medium", "intense", "medium", "light", "light", "light", "", "light", "", "", ""],
  };
}

function makeMurphy(): Recipe {
  return {
    intakes: [50, 48, 50, 47, 46, 45, 44, 43, 42, 40, 38, 38, 36, 11],
    behaviours: ["bright", "bright", "bright", "bright", "normal", "normal", "bright", "normal", "normal", "off", "off", "normal", "normal", "bright"],
    droppings: Array(14).fill("normal"),
    exMins: [60, 60, 45, 60, 45, 60, 45, 45, 60, 30, 45, 45, 30, 60],
    exInt: ["medium", "medium", "medium", "intense", "medium", "medium", "medium", "light", "medium", "light", "light", "medium", "light", "medium"],
  };
}

const RECIPES: Record<string, () => Recipe> = {
  bella: makeBella,
  henry: makeHenry,
  murphy: makeMurphy,
};

function statusForRatio(r: number): PaceStatus {
  if (r >= 0.9) return "hydrated";
  if (r >= 0.75) return "mild";
  if (r >= 0.55) return "moderate";
  if (r >= 0.30) return "severe";
  return "critical";
}

export function buildHorseHistory(horse: MockHorse): MockHistoryDay[] {
  const recipeFn = RECIPES[horse.id] ?? makeBella;
  const out = recipeFn();
  const len = out.intakes.length;
  const days: MockHistoryDay[] = [];
  const DAY_MS = 24 * 60 * 60 * 1000;
  for (let i = 0; i < len; i++) {
    const isToday = i === len - 1;
    const intake = out.intakes[i]!;
    let status: PaceStatus;
    if (isToday) {
      status = computePace(intake, horse.targetL, MOCK_NOW_HOUR).status;
    } else {
      const ratio = intake / horse.targetL;
      status = statusForRatio(ratio);
    }
    const date = new Date(MOCK_TODAY.getTime() - (len - 1 - i) * DAY_MS);
    days.push({
      date,
      dateLabel: date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
      dayShort: date.toLocaleDateString("en-GB", { weekday: "narrow" }),
      intakeL: intake,
      targetL: horse.targetL,
      status,
      behaviour: out.behaviours[i]!,
      droppings: out.droppings[i]!,
      exerciseMins: out.exMins[i]!,
      exerciseIntensity: out.exInt[i]!,
      isToday,
      partial: isToday,
    });
  }
  return days;
}

export const MOCK_HORSE_HISTORIES: Record<string, MockHistoryDay[]> = MOCK_HORSES.reduce(
  (acc, h) => {
    acc[h.id] = buildHorseHistory(h);
    return acc;
  },
  {} as Record<string, MockHistoryDay[]>
);

// Silence the imported-but-unused warning for paceFraction (kept in case
// downstream code wants to derive expected-by-now from intake).
void paceFraction;

// ----- Settings mock data -----

export interface MockMember {
  id: string;
  name: string;
  email: string;
  role: "Owner" | "Member";
  colour: HorseColour;
  joined: string;
  lastActive: string;
}

export interface MockPending {
  id: string;
  email: string;
  sent: string;
}

export const MOCK_TEAM = {
  ownerUserId: "u-sarah",
  currentUserId: "u-sarah",
  members: [
    { id: "u-sarah", name: "Sarah Hollins",  email: "sarah@hollowtreelivery.co.uk", role: "Owner",  colour: "bay",      joined: "2 Mar 2026",  lastActive: "just now" },
    { id: "u-pip",   name: "Pip Marsh",       email: "pip.marsh@gmail.com",          role: "Member", colour: "chestnut", joined: "5 Mar 2026",  lastActive: "2h ago" },
    { id: "u-tom",   name: "Tom Westcott",    email: "tom.westcott@gmail.com",       role: "Member", colour: "grey",     joined: "12 Apr 2026", lastActive: "yesterday" },
    { id: "u-beth",  name: "Beth Doyle",      email: "beth@doyleequine.com",         role: "Member", colour: "palomino", joined: "1 May 2026",  lastActive: "4d ago" },
  ] satisfies MockMember[],
  pending: [
    { id: "i-emma", email: "emma.howard@gmail.com", sent: "22 May · 3d ago" },
    { id: "i-vet",  email: "vet@grovevet.co.uk",    sent: "24 May · yesterday" },
  ] satisfies MockPending[],
};

export const MOCK_ACCOUNT = {
  name: "Sarah Hollins",
  email: "sarah@hollowtreelivery.co.uk",
  emailVerified: true,
};

export type WeightUnit = "kg" | "lb";
export type TempUnit = "C" | "F";
export type VolumeUnit = "L" | "gal";

export interface MockPrefs {
  weightUnit: WeightUnit;
  tempUnit: TempUnit;
  volumeUnit: VolumeUnit;
  timezone: string;
}

export const MOCK_PREFS: MockPrefs = {
  weightUnit: "kg",
  tempUnit: "C",
  volumeUnit: "L",
  timezone: "Europe/London",
};
